import React, { useState, useEffect, useRef } from "react";

import { connect } from "react-redux";

import { get } from "lodash";

import {
  Button,
  Dialog,
  Checkbox,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";

import PauseIcon from "@material-ui/icons/Pause";
import CachedIcon from "@material-ui/icons/Cached";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import { Org } from "lib/models/Org";

import Request from "lib/api/Request";

import ActionDialog from "views/Components/ActionDialog";

import { autoIsolationControlBarStyles } from "./controlBarStyles";

const threats = [
  { label: "P1 Threat", value: "threats:p1" },
  { label: "P2 Threat", value: "threats:p2" },
  { label: "P3 Threat", value: "threats:p3" },
];

const suspects = [
  { label: "P1 Suspect", value: "suspects:p1" },
  { label: "P2 Suspect", value: "suspects:p2" },
  { label: "P3 Suspect", value: "suspects:p3" },
];

const AutoIsolationControlBar = (props) => {
  const classes = autoIsolationControlBarStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [numberOfDevices, setNumberOfDevices] = useState(0);
  const [currentOrgModel, setCurrentOrgModel] = useState({});
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [selectedAutoIsolationTypes, setSelectedAutoIsolationTypes] = useState(
    []
  );

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };

  const prevOrgId = usePrevious(props.orgId);

  // refresh device data if org is switched
  useEffect(() => {
    if (prevOrgId !== props.orgId) {
      try {
        getDevices();
      } catch {
        setNumberOfDevices(0);
        setIsLoading(false);
      }
    }
  }, [props.orgId, prevOrgId]);

  useEffect(() => {
    if (props.currentOrg) {
      // orgs in session are stored as JSON, not models
      const newOrgModel = props.currentOrg.hash
        ? props.currentOrg
        : new Org(props.currentOrg);
      const autoIsolationTypes = newOrgModel.autoIsolationTypes
        ? newOrgModel.autoIsolationTypes
        : [];
      setSelectedAutoIsolationTypes(autoIsolationTypes);
      setCurrentOrgModel(newOrgModel);
    }
  }, [props.currentOrg]);

  const getDevices = async () => {
    setIsLoading(true);

    // get number of devices to display
    const request = new Request("/lcagent");
    const devices = await request.get();
    if (devices.length) {
      // only count isolated devices
      const numberOfIsolatedDevices = devices.filter(
        ({ isIsolated }) => !!isIsolated
      ).length;
      setNumberOfDevices(numberOfIsolatedDevices);
    }

    setIsLoading(false);
  };

  const handleSetIsolationClick = async () => {
    if (currentOrgModel.autoIsolationEnabled) {
      setIsConfirmationDialogOpen(true);
    } else {
      handleSaveHostIsolation();
    }
  };

  const handleDisableHostIsolation = async () => {
    handleSaveHostIsolation();
  };

  const handleSettingsClick = () => {
    setIsSettingsDialogOpen(true);
  };

  const handleDevicesClick = () => {
    props.routeToDevicesPageView(props.orgId);
  };

  const handleRefreshClick = () => {
    try {
      getDevices();
    } catch {
      setNumberOfDevices(0);
      setIsLoading(false);
    }
  };

  const handleSelectType = (event) => {
    const selectedType = event.target.value;

    const isTypeSelected = selectedAutoIsolationTypes.find(
      (type) => type === selectedType
    );

    let newAutoIsolationTypes = isTypeSelected
      ? selectedAutoIsolationTypes.filter((type) => type !== selectedType)
      : selectedAutoIsolationTypes.concat([selectedType]);

    setSelectedAutoIsolationTypes(newAutoIsolationTypes);
  };

  const isTypeChecked = (selectedType) => {
    return !!selectedAutoIsolationTypes.find((type) => type === selectedType);
  };

  const saveOrg = async (orgModel) => {
    try {
      setIsLoading(true);
      await orgModel.update().then((org) => {
        setCurrentOrgModel(org);
      });
      setIsLoading(false);
      setIsSettingsDialogOpen(false);
      setIsConfirmationDialogOpen(false);
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      setCurrentOrgModel(currentOrgModel);
      setIsSettingsDialogOpen(false);
      setIsConfirmationDialogOpen(false);
    }
  };

  const handleSaveSettings = () => {
    let orgModelToUpdate = currentOrgModel;
    orgModelToUpdate.set({ autoIsolationTypes: selectedAutoIsolationTypes });
    saveOrg(orgModelToUpdate);
  };

  const handleSaveHostIsolation = () => {
    let orgModelToUpdate = currentOrgModel;
    orgModelToUpdate.set({
      autoIsolationEnabled: !currentOrgModel.autoIsolationEnabled,
    });
    saveOrg(orgModelToUpdate);
  };

  return (
    <div className={classes.controlBarWrap}>
      <div className={classes.innerWrap}>
        <Typography className={classes.controlBarText}>
          {currentOrgModel.autoIsolationEnabled
            ? "Automated Host Isolation is running"
            : "Automated Host Isolation is disabled"}
        </Typography>
        <Button
          variant={"outlined"}
          className={classes.button}
          onClick={handleSetIsolationClick}
          startIcon={
            isLoading ? null : currentOrgModel.autoIsolationEnabled ? (
              <PauseIcon className={classes.buttonText} />
            ) : (
              <PlayArrowIcon className={classes.buttonText} />
            )
          }
        >
          {isLoading ? (
            <CircularProgress size={12} className={classes.buttonText} />
          ) : (
            <p className={classes.buttonText}>
              {currentOrgModel.autoIsolationEnabled ? "Pause" : "Start"}
            </p>
          )}
        </Button>
        <Button
          variant={"outlined"}
          className={classes.button}
          onClick={handleSettingsClick}
        >
          <p className={classes.buttonText}>Settings</p>
        </Button>
        <Button
          variant={"outlined"}
          className={classes.countButton}
          onClick={handleDevicesClick}
        >
          {isLoading ? (
            <CircularProgress size={12} className={classes.buttonText} />
          ) : (
            <p className={classes.buttonText}>
              {numberOfDevices} device
              {numberOfDevices === 0 || numberOfDevices === 0 > 1
                ? "s"
                : ""}{" "}
              currently isolated
            </p>
          )}
        </Button>
        <Button
          variant={"outlined"}
          onClick={handleRefreshClick}
          className={classes.refreshButton}
          startIcon={<CachedIcon className={classes.buttonText} />}
        >
          <p className={classes.buttonText}>Refresh</p>
        </Button>
      </div>
      <ActionDialog
        title={`Are you sure?`}
        open={isConfirmationDialogOpen}
        description={
          "Automated Host Isolation will remain disabled until you restart it."
        }
        actions={[
          {
            title: "Cancel",
            action: () => setIsConfirmationDialogOpen(false),
          },
          {
            title: "Disable Automated Host Isolation",
            action: handleDisableHostIsolation,
            variant: "contained",
          },
        ]}
      />
      <Dialog open={isSettingsDialogOpen} maxWidth={"sm"} fullWidth={true}>
        <DialogTitle>Automated Host Isolation Settings</DialogTitle>
        <DialogContent>
          <div className={classes.checkboxOuterContainer}>
            {threats.map((threat) => (
              <div
                key={threat.value}
                className={classes.checkboxInnerContainer}
              >
                <Checkbox
                  color={"primary"}
                  value={threat.value}
                  onChange={handleSelectType}
                  checked={isTypeChecked(threat.value)}
                />
                <p>{threat.label}</p>
              </div>
            ))}
          </div>
          <div className={classes.checkboxOuterContainer}>
            {suspects.map((suspect) => (
              <div
                key={suspect.value}
                className={classes.checkboxInnerContainer}
              >
                <Checkbox
                  color={"primary"}
                  value={suspect.value}
                  onChange={handleSelectType}
                  checked={isTypeChecked(suspect.value)}
                />
                <p>{suspect.label}</p>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          {/* TODO: add documentation link when complete */}
          <a href={""} rel={"noopener noreferrer"} target={"_blank"}>
            Documentation <OpenInNewIcon />
          </a>
          <div>
            <Button
              color={"primary"}
              variant={"text"}
              onClick={() => setIsSettingsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color={"primary"}
              variant={"contained"}
              onClick={handleSaveSettings}
              disabled={
                isLoading ||
                !selectedAutoIsolationTypes.length ||
                currentOrgModel.autoIsolationTypes ===
                  selectedAutoIsolationTypes
              }
            >
              {isLoading ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => {
  const currentOrgId = state.location.payload.orgId;
  const userOrgs = get(state, ["session", "settings", "userOrgs"], []);
  const currentOrg = userOrgs.find(({ id }) => id === currentOrgId);

  return {
    currentOrg,
    orgId: currentOrgId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  routeToDevicesPageView: (orgId) => {
    dispatch({
      type: "PAGE",
      payload: {
        orgId,
        toplevel: "blumiraagent",
        secondlevel: "devices",
        query: { isIsolated: true },
      },
    });
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AutoIsolationControlBar);
